import React, { useState, useEffect, useCallback } from "react";
import { apiGet } from "../../services/api";
import SkeletonLoader from "./SkeletonLoader";
import ClinicIcon from "./ClinicIcon";
import { toIsoDate, statusClass } from "../careflow/utils";
import "./MobileTaskView.css";

interface QueueItem {
  visit_id?: number;
  patient_id: string | number;
  patient_name?: string;
  status?: string;
  visit_type?: string;
  doctor_name?: string | null;
}

interface MobileTaskViewProps {
  role: "reception" | "doctor";
  patient?: { patient_id?: string | number; name?: string; age?: string | number } | null;
  recallsCount?: number;
  onlineCount?: number;
  onPatientSelect?: (p: { patient_id: string | number; name?: string }) => void;
  onOpenNotifications?: () => void;
  onOpenPatientSearch?: () => void;
  onEndSession?: () => void;
}

type NavSection = "queue" | "patient" | "actions" | "notifications";

function getInitials(name?: string) {
  if (!name) return "??";
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function MobileTaskView({
  role,
  patient,
  recallsCount = 0,
  onlineCount = 0,
  onPatientSelect,
  onOpenNotifications,
  onOpenPatientSearch,
  onEndSession,
}: MobileTaskViewProps) {
  const [section, setSection] = useState<NavSection>("queue");
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [loadingQueue, setLoadingQueue] = useState(false);

  const loadQueue = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const data = (await apiGet(
        `/api/reception/today-queue?date=${toIsoDate()}`
      )) as QueueItem[];
      setQueue(Array.isArray(data) ? data : []);
    } catch {
      setQueue([]);
    } finally {
      setLoadingQueue(false);
    }
  }, []);

  useEffect(() => {
    if (section === "queue") void loadQueue();
  }, [section, loadQueue]);

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "short", month: "short", day: "numeric",
  });

  const notifCount = recallsCount + onlineCount;

  return (
    <div className="mtv-root" role="main">
      {/* Top bar */}
      <div className="mtv-topbar">
        <span className="mtv-role">
          {role === "doctor" ? "Doctor Station" : "Front Desk"}
        </span>
        <span className="mtv-date">{today}</span>
        <button
          type="button"
          className="mtv-fullscreen-btn"
          onClick={onOpenPatientSearch}
          title="Search patient"
          aria-label="Search patient"
        >
          <ClinicIcon name="search" size={16} />
        </button>
      </div>

      {/* Active patient strip */}
      <div className={`mtv-patient-bar${patient ? "" : " no-patient"}`}>
        {patient ? (
          <>
            <div className="mtv-patient-avatar">{getInitials(patient.name)}</div>
            <div className="mtv-patient-info">
              <div className="mtv-patient-name">{patient.name || "Patient"}</div>
              <div className="mtv-patient-meta">
                #{patient.patient_id}{patient.age ? ` · ${patient.age} yrs` : ""}
              </div>
            </div>
            {onEndSession && (
              <button type="button" className="mtv-fullscreen-btn" onClick={onEndSession}
                title="End session" aria-label="End patient session">
                  <ClinicIcon name="close" size={16} />
              </button>
            )}
          </>
        ) : (
          <span className="mtv-no-patient">No active patient — tap 🔍 to search</span>
        )}
      </div>

      {/* Content */}
      <div className="mtv-content">
        {section === "queue" && (
          <>
            <div className="mtv-section-title">Today&apos;s Queue</div>
            {loadingQueue ? (
              <SkeletonLoader variant="list" rows={5} />
            ) : queue.length === 0 ? (
              <div style={{ color: "rgba(255,255,255,0.25)", fontSize: "0.82rem", textAlign: "center", marginTop: 24 }}>
                Queue is empty for today
              </div>
            ) : queue.map((v) => (
              <div
                key={v.visit_id ?? String(v.patient_id)}
                className="mtv-queue-item"
                onClick={() => onPatientSelect?.({ patient_id: String(v.patient_id), name: v.patient_name })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    onPatientSelect?.({ patient_id: String(v.patient_id), name: v.patient_name });
                }}
                aria-label={`Select patient ${v.patient_name || v.patient_id}`}
              >
                <div className="mtv-queue-avatar">{getInitials(v.patient_name)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mtv-queue-name">{v.patient_name || "Unknown"}</div>
                  <div className="mtv-queue-meta">
                    #{v.patient_id} · {v.visit_type || "Walk-in"}
                    {v.doctor_name ? ` · ${v.doctor_name}` : ""}
                  </div>
                </div>
                <div className={`mtv-queue-status ${statusClass(v.status)}`}>
                  {v.status || "Open"}
                </div>
              </div>
            ))}
          </>
        )}

        {section === "actions" && (
          <>
            <div className="mtv-section-title">Quick Actions</div>
            <div className="mtv-action-grid">
              <div className="mtv-action-card-wrap">
                <div className="mtv-action-card" onClick={onOpenPatientSearch} role="button" tabIndex={0}>
                  <span className="mtv-action-icon"><ClinicIcon name="search" size={24} /></span>
                  <span className="mtv-action-label">Search Patient</span>
                </div>
              </div>
              <div className="mtv-action-card-wrap">
                <div className="mtv-action-card" onClick={onOpenNotifications} role="button" tabIndex={0}>
                  <span className="mtv-action-icon"><ClinicIcon name="bell" size={24} /></span>
                  <span className="mtv-action-label">Notifications</span>
                  {notifCount > 0 && <div className="mtv-action-badge">{notifCount}</div>}
                </div>
              </div>
              <div className="mtv-action-card-wrap">
                <div className="mtv-action-card"
                  onClick={() => { void loadQueue(); setSection("queue"); }}
                  role="button" tabIndex={0}>
                  <span className="mtv-action-icon"><ClinicIcon name="history" size={24} /></span>
                  <span className="mtv-action-label">Refresh Queue</span>
                </div>
              </div>
            </div>
          </>
        )}

        {section === "patient" && (
          <>
            <div className="mtv-section-title">Patient</div>
            {patient ? (
              <div style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.82rem" }}>
                <p><strong style={{ color: "#e2e8f0" }}>{patient.name}</strong></p>
                <p>ID: #{patient.patient_id}</p>
                {patient.age && <p>Age: {patient.age} years</p>}
                {onEndSession && (
                  <button type="button" onClick={onEndSession}
                    style={{
                      marginTop: 16, padding: "10px 16px", borderRadius: 8,
                      border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.1)",
                      color: "#fca5a5", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", width: "100%"
                    }}>
                    End Session
                  </button>
                )}
              </div>
            ) : (
              <div style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.82rem" }}>
                No patient selected. Search to begin a session.
              </div>
            )}
          </>
        )}

        {section === "notifications" && (
          <>
            <div className="mtv-section-title">Notifications</div>
            <div className="mtv-action-grid">
              {recallsCount > 0 && (
                <div className="mtv-action-card-wrap">
                  <div className="mtv-action-card" onClick={onOpenNotifications} role="button" tabIndex={0}>
                    <span className="mtv-action-icon"><ClinicIcon name="queue" size={24} /></span>
                    <span className="mtv-action-label">Recalls ({recallsCount})</span>
                  </div>
                </div>
              )}
              {onlineCount > 0 && (
                <div className="mtv-action-card-wrap">
                  <div className="mtv-action-card" onClick={onOpenNotifications} role="button" tabIndex={0}>
                    <span className="mtv-action-icon"><ClinicIcon name="online" size={24} /></span>
                    <span className="mtv-action-label">Online ({onlineCount})</span>
                  </div>
                </div>
              )}
              {notifCount === 0 && (
                <div style={{ color: "rgba(255,255,255,0.25)", fontSize: "0.82rem", display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ClinicIcon name="check" size={16} /> All clear — no pending notifications
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Bottom navigation */}
      <nav className="mtv-bottom-nav" aria-label="Mobile navigation">
        {([
          { key: "queue", icon: "menu", label: "Queue" },
          { key: "patient", icon: "user", label: "Patient" },
          { key: "actions", icon: "settings", label: "Actions" },
          { key: "notifications", icon: "bell", label: notifCount > 0 ? `Alerts (${notifCount})` : "Alerts" },
        ] as { key: NavSection; icon: string; label: string }[]).map((item) => (
          <button
            key={item.key}
            type="button"
            className={`mtv-nav-btn${section === item.key ? " active" : ""}`}
            onClick={() => setSection(item.key)}
            aria-current={section === item.key ? "page" : undefined}
          >
            <span className="mtv-nav-icon"><ClinicIcon name={item.icon as any} size={20} /></span>
            {item.label}
          </button>
        ))}
      </nav>
    </div>
  );
}
